import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '../utils/supabase';
import { normalizeSupabaseAnonKey, normalizeSupabaseUrl } from '../utils/supabaseEnv';
import { User, Session } from '@supabase/supabase-js';
import { dataCache } from '../utils/dataCache';
import * as Sentry from '@sentry/react';
import { setUserContextProvider } from '../utils/setupLogging';

const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const MIN_PASSWORD_LENGTH = 8;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  isRecovery: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getAuthErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  
  const message = error?.message || error?.error?.message || error?.toString() || '';
  const errorCode = error?.code || error?.status || error?.error?.code || '';
  const errorString = JSON.stringify(error).toLowerCase();
  
  const duplicateEmailIndicators = [
    'User already registered',
    'already registered',
    'already exists',
    'email address is already',
    'Email already registered',
    'A user with this email already exists',
    'user with this email address has already been registered',
    'signup_disabled',
    'email_already_exists',
  ];
  
  const isDuplicateEmail = 
    duplicateEmailIndicators.some(indicator => 
      message.toLowerCase().includes(indicator.toLowerCase()) ||
      errorString.includes(indicator.toLowerCase())
    ) ||
    errorCode === 'signup_disabled' ||
    errorCode === 'email_already_exists' ||
    (error?.message?.toLowerCase().includes('already'));
  
  if (isDuplicateEmail) {
    return 'An account with this email already exists. Please sign in instead.';
  }
  
  if (message.includes('Invalid login credentials')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }
  
  if (message.includes('Email not confirmed')) {
    return 'Please check your email and click the confirmation link to activate your account.';
  }
  
  if (message.includes('Password should be at least')) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`;
  }
  
  if (message.includes('Unable to validate email address')) {
    return 'Please enter a valid email address.';
  }
  
  if (message.includes('Signup is disabled')) {
    return 'Account creation is currently disabled. Please contact support.';
  }
  
  if (message.includes('Too many requests')) {
    return 'Too many login attempts. Please wait a moment before trying again.';
  }
  
  return message || 'Failed to create account. Please try again.';
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRecovery, setIsRecovery] = useState(false);
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    const supabaseUrl = normalizeSupabaseUrl(import.meta.env.VITE_SUPABASE_URL);
    const supabaseKey = normalizeSupabaseAnonKey(import.meta.env.VITE_SUPABASE_ANON_KEY);
    
    if (!supabaseUrl || !supabaseKey || supabaseUrl.includes('placeholder') || supabaseKey.includes('placeholder')) {
      console.error('Missing or invalid Supabase environment variables. Please check your .env file.');
      setError('Missing Supabase configuration. Please check your environment variables.');
      setIsLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      const initialUser = session?.user ?? null;
      setUser(initialUser);
      userIdRef.current = initialUser?.id ?? null;
      setIsLoading(false);
      
      if (initialUser) {
        Sentry.setUser({
          id: initialUser.id,
          email: initialUser.email || undefined,
        });
        setUserContextProvider(() => ({ userId: initialUser.id }));
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }
      if (event === 'SIGNED_OUT') {
        setIsRecovery(false);
        if (userIdRef.current) {
          dataCache.clearUserCache(userIdRef.current);
        }
      }
      if (event === 'USER_UPDATED') {
        setIsRecovery(false);
      }

      setSession(session);
      const newUser = session?.user ?? null;
      setUser(newUser);
      userIdRef.current = newUser?.id ?? null;
      setIsLoading(false);
      
      if (newUser) {
        Sentry.setUser({
          id: newUser.id,
          email: newUser.email || undefined,
        });
      } else {
        Sentry.setUser(null);
      }
      
      setUserContextProvider(() => newUser ? { userId: newUser.id } : undefined);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || isRecovery) return;

    let timer: ReturnType<typeof setTimeout>;
    const bump = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        void supabase.auth.signOut();
      }, IDLE_TIMEOUT_MS);
    };

    const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, bump, { passive: true }));
    bump();

    return () => {
      clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, bump));
    };
  }, [user, isRecovery]);

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw new Error(getAuthErrorMessage(error));
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      throw new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });
      
      if (error) {
        throw new Error(getAuthErrorMessage(error));
      }
      
      if (!data?.user) {
        throw new Error('Failed to create account. Please try again.');
      }
    } catch (error: any) {
      setIsLoading(false);
      
      if (error?.message) {
        throw error;
      }
      
      throw new Error(error?.toString() || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw new Error('Failed to sign out. Please try again.');
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const requestPasswordReset = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      throw new Error(getAuthErrorMessage(error));
    }
  };

  const updatePassword = async (password: string) => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      throw new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
    }
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      throw new Error(getAuthErrorMessage(error));
    }
    setIsRecovery(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      signIn,
      signUp,
      signOut,
      requestPasswordReset,
      updatePassword,
      isLoading,
      error,
      isRecovery,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
